import { AuthStep } from '@/bundle/Auth/ui/AuthStep/AuthStep';
import { Resend, ResendWrapper, TwoFALabel } from './styles';
import { Notification } from '@/components/Notification/Notification';
import { TwoFaCodeInput } from '../TwoFaCodeInput/TwoFaCodeInput';
import { Timer } from '@/bundle/Auth/LoginFlow/TwoFAPage/ui/Timer/Timer';
import { Button } from '@/components/Button/Button';
import { forwardRef, useEffect, useState } from 'react';
import { authTokenService } from '@/bundle/shared/services/authTokenService';
import { decodeToken } from '@/helpers/tokenHelpers';
import { AuthTokenType, ResponseErrorType } from '@/types/sharedTypes';
import { formatPhone } from '@/helpers/formatHelpers';
import { Loader } from '@/components/Loader/Loader';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { Box } from '@/components/Box/Box';
import { useTranslation } from 'react-i18next';
import { useAuthNavigateToLogin } from '@/bundle/Auth/hooks/useAuthNavigateToLogin';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { useVerificationCode } from '@/bundle/shared/hooks/useVerificationCode';

type TwoFAFormType = {
  isSendCodeLoading: boolean;
  isTwoFALoading: boolean;
  onConfirm: (code: string) => void;
  onResend: () => void;
  twoFAError?: ResponseErrorType;
  sendCodeError?: ResponseErrorType;
};

export const TwoFAForm = forwardRef((props: TwoFAFormType, ref) => {
  const { isSendCodeLoading, isTwoFALoading, twoFAError, sendCodeError, onConfirm, onResend } = props;
  const { t } = useTranslation('login');
  const { authNavigateToLogin } = useAuthNavigateToLogin();

  const [phone, setPhone] = useState('');
  const {
    codeChanged,
    isDisabledSendCode,
    seconds,
    minutes,
    code,
    startTimer,
    setSeconds,
    setMinutes,
    changeCode,
    isDisabledSubmit,
    onSubmitCode,
  } = useVerificationCode(ref, onResend, onConfirm);

  const setUserPhone = () => {
    const tokens = authTokenService.getTokens();

    if (!tokens) return;

    const { mobile_phone } = decodeToken<AuthTokenType>(tokens.access);

    setPhone(formatPhone(mobile_phone));
  };

  useEffect(() => {
    setUserPhone();
    onResend();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const apiError = getResponseError(twoFAError) || getResponseError(sendCodeError);
  const hasInputError = !!getResponseError(twoFAError) && !codeChanged;

  return (
    <AuthStep width='508px' top={0}>
      <Typography variant='headline4' fontWeight='semibold' color={COLORS.grey[900]} marginBottom='12px'>
        {t('login:2faHeader')}
      </Typography>
      <Typography color={COLORS.grey[950]}>{t('login:2faHeaderMessage')}</Typography>
      <Typography fontWeight='semibold' color={COLORS.grey[950]} marginBottom='12px'>
        {phone}.
      </Typography>
      <Typography color={COLORS.grey[600]} marginBottom='14px'>
        {t('login:2faValidPhoneMessage')}
      </Typography>

      {apiError && (
        <Notification variant='error' marginTop='12px' marginBottom='12px'>
          {apiError}
        </Notification>
      )}

      <form onSubmit={onSubmitCode}>
        <Box position='relative' marginBottom='64px'>
          <TwoFALabel>{t('login:2faInputLabel')}</TwoFALabel>
          <TwoFaCodeInput code={code} onChange={changeCode} hasError={hasInputError} />
          <ResendWrapper>
            {isDisabledSendCode && (
              <Timer minutes={minutes} setMinutes={setMinutes} seconds={seconds} setSeconds={setSeconds} />
            )}
            {isSendCodeLoading ? (
              <Loader inline backgroundColor='transparent' />
            ) : (
              <Resend type='button' onClick={startTimer} isDisabled={isDisabledSendCode}>
                {t('login:2faResendButtonLabel')}
              </Resend>
            )}
          </ResendWrapper>
        </Box>
        <Box display='flex' flexDirection='column' rowGap='12px'>
          <Button type='submit' disabled={isDisabledSubmit} isLoading={isTwoFALoading}>
            {t('login:submitButtonLabel')}
          </Button>
          <Button variant='secondary' onClick={authNavigateToLogin}>
            {t('login:returnToLoginButtonLabel')}
          </Button>
        </Box>
      </form>
    </AuthStep>
  );
});
